var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { buildDataZoom, buildLegend, buildTooltip, mergeCategorySettings, } from "./shared";
var GRID_SIDE_PADDING = 40;
var AXIS_OFFSET_STEP = 50;
var DEFAULT_COLORS = [
    "#5470C6",
    "#91CC75",
    "#EE6666",
    "#FAC858",
    "#73C0DE",
    "#3BA272",
    "#FC8452",
    "#9A60B4",
    "#EA7CCC",
];
function hexToRgba(hex, alpha) {
    if (!hex.startsWith("#")) {
        return undefined;
    }
    var normalized = hex.length === 4
        ? "#".concat(hex[1]).concat(hex[1]).concat(hex[2]).concat(hex[2]).concat(hex[3]).concat(hex[3])
        : hex;
    var bigint = parseInt(normalized.slice(1), 16);
    if (Number.isNaN(bigint)) {
        return undefined;
    }
    var r = (bigint >> 16) & 255;
    var g = (bigint >> 8) & 255;
    var b = bigint & 255;
    return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(alpha, ")");
}
var DEFAULT_AXIS_ID = "left";
function ensureSeriesValues(categories, data) {
    if (!data) {
        return categories.map(function () { return null; });
    }
    if (data.length >= categories.length) {
        return data.slice(0, categories.length);
    }
    return __spreadArray(__spreadArray([], data, true), Array(categories.length - data.length).fill(null), true);
}
// 使用一个很小的非零值作为占位符，确保 ECharts 能计算 layout
// 使用 0.01 而不是 0，避免被 ECharts 判断为无效数据
var CANDLESTICK_PLACEHOLDER = [0.01, 0.01, 0.01, 0.01];
/**
 * 处理 K 线图数据格式
 * K 线图需要 [开盘, 收盘, 最低, 最高] 格式
 * 若缺数据则用占位 [0,0,0,0]，避免 ECharts progress 回调中 getItemLayout(i).sign 报错
 */
function ensureCandlestickValues(categories, data) {
    var _a, _b, _c, _d;
    if (!data) {
        return categories.map(function () { return __spreadArray([], CANDLESTICK_PLACEHOLDER, true); });
    }
    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
        var klineData = data;
        var out = [];
        for (var i = 0; i < categories.length; i++) {
            var row = klineData[i];
            if (Array.isArray(row) && row.length >= 4) {
                var open = Number(row[0]);
                var close = Number(row[1]);
                var low = Number(row[2]);
                var high = Number(row[3]);
                // 确保所有值都是有效的有限数字
                // 对于 K 线图，基本要求：最低价 <= 最高价
                // 放宽验证条件，只要数据是有效的数字且 low <= high 就接受
                if (Number.isFinite(open) &&
                    Number.isFinite(close) &&
                    Number.isFinite(low) &&
                    Number.isFinite(high)) {
                    // 确保 low <= high，如果不符合则交换
                    var finalLow = Math.min(low, high);
                    var finalHigh = Math.max(low, high);
                    // 直接使用原始数据，不进行调整
                    // ECharts 可以处理不符合传统K线图逻辑的数据
                    out.push([open, close, finalLow, finalHigh]);
                }
                else {
                    // 如果数据无效，使用占位符
                    console.warn('[CompsChart] K线图数据无效，使用占位符:', { open: open, close: close, low: low, high: high, row: row });
                    out.push(__spreadArray([], CANDLESTICK_PLACEHOLDER, true));
                }
            }
            else {
                out.push(__spreadArray([], CANDLESTICK_PLACEHOLDER, true));
            }
        }
        return out;
    }
    if (Array.isArray(data) && typeof data[0] === "number") {
        var flatData = data;
        var out = [];
        for (var i = 0; i < categories.length; i++) {
            var base = i * 4;
            if (base + 3 < flatData.length) {
                var open = Number((_a = flatData[base]) !== null && _a !== void 0 ? _a : 0);
                var close = Number((_b = flatData[base + 1]) !== null && _b !== void 0 ? _b : 0);
                var low = Number((_c = flatData[base + 2]) !== null && _c !== void 0 ? _c : 0);
                var high = Number((_d = flatData[base + 3]) !== null && _d !== void 0 ? _d : 0);
                // 确保所有值都是有效的有限数字
                if (Number.isFinite(open) && Number.isFinite(close) && Number.isFinite(low) && Number.isFinite(high)) {
                    out.push([open, close, low, high]);
                }
                else {
                    out.push(__spreadArray([], CANDLESTICK_PLACEHOLDER, true));
                }
            }
            else {
                out.push(__spreadArray([], CANDLESTICK_PLACEHOLDER, true));
            }
        }
        return out;
    }
    return categories.map(function () { return __spreadArray([], CANDLESTICK_PLACEHOLDER, true); });
}
function resolveAxisConfigs(data) {
    var _a;
    var axisConfigs = data.axes || (_a = {},
        _a[DEFAULT_AXIS_ID] = {
            id: DEFAULT_AXIS_ID,
            position: "left",
        },
        _a);
    var axisEntries = Object.values(axisConfigs);
    // 调试：检查轴配置
    if (process.env.NODE_ENV === 'development') {
        console.log('[CompsChart] resolveAxisConfigs 输入:', {
            axes: data.axes,
            axisConfigs: axisConfigs,
            axisEntriesCount: axisEntries.length,
            axisEntries: axisEntries.map(function (a) { return ({ id: a.id, position: a.position, name: a.name }); }),
        });
    }
    var axisIndexMap = new Map();
    var sideOffsetMax = {
        left: 0,
        right: 0,
    };
    var positionCounters = {};
    var yAxis = axisEntries.map(function (axis, index) {
        var _a, _b;
        axisIndexMap.set(axis.id, index);
        var position = (_a = axis.position) !== null && _a !== void 0 ? _a : (index === 0 ? "left" : "right");
        var counter = (_b = positionCounters[position]) !== null && _b !== void 0 ? _b : 0;
        positionCounters[position] = counter + 1;
        var offsetValue = position === "left" || position === "right"
            ? counter * AXIS_OFFSET_STEP
            : 0;
        if ((position === "left" || position === "right") &&
            offsetValue > sideOffsetMax[position]) {
            sideOffsetMax[position] = offsetValue;
        }
        // 透传业务侧 axis.axisLine（含 show / lineStyle），仅认 showAxisLine 会丢掉 OrderTraffic 等配置
        var fromAxisLine = axis.axisLine && typeof axis.axisLine === "object"
            ? __assign({}, axis.axisLine) : {};
        var axisLine = __assign({}, fromAxisLine);
        if (axis.showAxisLine !== undefined) {
            axisLine.show = axis.showAxisLine;
        }
        var splitLine = {
            show: index === 0,
        };
        return {
            type: "value",
            id: axis.id,
            position: position,
            name: axis.name,
            // 如果未设置 min，右侧轴默认从0开始，左侧轴使用 "dataMin" 以自动根据数据调整起始坐标
            min: axis.min !== undefined
                ? axis.min
                : (position === "right" ? 0 : "dataMin"),
            // 如果未设置 max，默认使用 "dataMax" 以自动根据数据调整结束坐标
            max: axis.max !== undefined ? axis.max : "dataMax",
            splitNumber: axis.splitNumber,
            axisLabel: axis.axisLabel && axis.axisLabel.formatter
                ? { formatter: axis.axisLabel.formatter }
                : undefined,
            axisLine: Object.keys(axisLine).length > 0 ? axisLine : undefined,
            splitLine: Object.keys(splitLine).length > 0 ? splitLine : undefined,
            offset: offsetValue > 0 ? offsetValue : undefined,
        };
    });
    // 调试：检查轴解析结果
    if (process.env.NODE_ENV === 'development') {
        console.log('[CompsChart] resolveAxisConfigs 输出:', {
            yAxisCount: yAxis.length,
            yAxis: yAxis.map(function (a) { return ({ id: a.id, position: a.position, name: a.name, min: a.min, max: a.max }); }),
            axisIndexMap: Array.from(axisIndexMap.entries()),
        });
    }
    return { yAxis: yAxis, axisIndexMap: axisIndexMap, sideOffsetMax: sideOffsetMax };
}
function attachAxisBoundsBySeries(yAxis, seriesConfig, dataset, categories) {
    var axisValueMap = new Map();
    seriesConfig.forEach(function (series) {
        var _a, _b;
        var axisId = (_a = series.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
        var data = dataset[series.dataKey];
        var values;
        if (series.type === "candlestick" && Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
            // K 线图：从二维数组中提取收盘价（索引 1）
            values = data
                .map(function (candle) { return (Array.isArray(candle) && typeof candle[1] === "number" ? candle[1] : null); })
                .filter(function (value) { return typeof value === "number"; });
        }
        else {
            // 普通图表：使用一维数组
            values = ensureSeriesValues(categories, data)
                .filter(function (value) { return typeof value === "number"; })
                .map(function (value) { return value; });
        }
        if (!axisValueMap.has(axisId)) {
            axisValueMap.set(axisId, []);
        }
        (_b = axisValueMap.get(axisId)) === null || _b === void 0 ? void 0 : _b.push.apply(_b, values);
    });
    return yAxis.map(function (axis) {
        var _a;
        // 如果已经设置了 min 或 max，或者没有数据，直接返回
        if ((axis.min !== undefined && axis.min !== null && axis.min !== "dataMin") ||
            (axis.max !== undefined && axis.max !== null && axis.max !== "dataMax") ||
            !axisValueMap.has(axis.id)) {
            return axis;
        }
        var values = (_a = axisValueMap.get(axis.id)) !== null && _a !== void 0 ? _a : [];
        if (!values.length) {
            return axis;
        }
        // 只有当 min/max 未设置或为 "dataMin"/"dataMax" 时才自动计算
        // 这里保持使用 "dataMin"/"dataMax"，让 ECharts 自动处理，避免下方空白
        return __assign(__assign({}, axis), { min: (axis.min === undefined || axis.min === null || axis.min === "dataMin") ? "dataMin" : axis.min, max: (axis.max === undefined || axis.max === null || axis.max === "dataMax") ? "dataMax" : axis.max });
    });
}
function buildSeries(ctx, mergedSettings, axisIndexMap, layout) {
    var _a;
    var data = ctx.data;
    var isHorizontal = layout === "horizontal";
    // 确保 series 存在，如果不存在则返回空数组
    if (!data.series || !Array.isArray(data.series)) {
        return [];
    }
    // 调试：检查输入的series数据
    if (process.env.NODE_ENV === 'development') {
        var klineSeriesInInput = data.series.find(function (s) { return s.type === 'candlestick'; });
        console.log('[CompsChart] buildSeries 输入检查:', {
            totalSeries: data.series.length,
            hasKlineSeries: !!klineSeriesInInput,
            klineSeriesInfo: klineSeriesInInput ? {
                id: klineSeriesInInput.id,
                type: klineSeriesInInput.type,
                dataKey: klineSeriesInInput.dataKey,
                barWidth: klineSeriesInInput.barWidth,
            } : null,
            allSeriesTypes: data.series.map(function (s) { return ({ id: s.id, type: s.type, dataKey: s.dataKey }); }),
        });
    }
    var comboSeries = data.series.map(function (series, seriesIndex) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        var axisId = (_a = series.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
        var valueAxisIndex = (_b = axisIndexMap.get(axisId)) !== null && _b !== void 0 ? _b : 0;
        var type = series.type;
        var resolvedColor = (_c = series.color) !== null && _c !== void 0 ? _c : DEFAULT_COLORS[seriesIndex % DEFAULT_COLORS.length];
        // K 线图需要特殊的数据格式处理
        var values;
        if (type === "candlestick") {
            var rawData = data.dataset[series.dataKey];
            values = ensureCandlestickValues(data.categories, rawData);
            // 确保数据长度与 categories 完全匹配，避免维度映射错误
            if (values.length !== data.categories.length) {
                var padded = __spreadArray([], values, true);
                while (padded.length < data.categories.length) {
                    padded.push(__spreadArray([], CANDLESTICK_PLACEHOLDER, true));
                }
                values = padded.slice(0, data.categories.length);
            }
            // 调试：检查K线图数据
            if (process.env.NODE_ENV === 'development') {
                var hasValidData = values.some(function (v) {
                    return v && v.length === 4 && v.some(function (n) { return n > 0.1; });
                } // 降低阈值，只要大于0.1就认为有效
                );
                var placeholderCount = values.filter(function (v) {
                    return v && v.length === 4 && v[0] === 0.01 && v[1] === 0.01 && v[2] === 0.01 && v[3] === 0.01;
                }).length;
                var validDataCount = values.filter(function (v) {
                    return v && v.length === 4 && v.some(function (n) { return n > 0.1; });
                }).length;
                console.log('[CompsChart] K线图数据处理结果:', {
                    dataKey: series.dataKey,
                    rawDataLength: rawData === null || rawData === void 0 ? void 0 : rawData.length,
                    categoriesLength: data.categories.length,
                    processedValuesLength: values.length,
                    hasValidData: hasValidData,
                    validDataCount: validDataCount,
                    placeholderCount: placeholderCount,
                    firstRawData: rawData === null || rawData === void 0 ? void 0 : rawData[0],
                    firstProcessedValue: values[0],
                    sampleProcessedValues: values.slice(0, 3),
                    // 检查开盘价和收盘价的差异
                    openCloseDiff: values.map(function (v) {
                        if (v && v.length >= 2) {
                            return Math.abs(v[1] - v[0]); // 收盘价 - 开盘价的绝对值
                        }
                        return 0;
                    }).slice(0, 3),
                    // 检查最低价和最高价的差异
                    lowHighDiff: values.map(function (v) {
                        if (v && v.length >= 4) {
                            return v[3] - v[2]; // 最高价 - 最低价
                        }
                        return 0;
                    }).slice(0, 3),
                });
                if (!hasValidData && rawData && rawData.length > 0) {
                    console.warn('[CompsChart] ⚠️ K线图数据可能无效，所有数据都被替换为占位符', {
                        rawDataSample: rawData.slice(0, 3),
                        processedValuesSample: values.slice(0, 3),
                    });
                }
                if (placeholderCount > 0) {
                    console.warn('[CompsChart] ⚠️ K线图有', placeholderCount, '个数据点被替换为占位符 [0.01, 0.01, 0.01, 0.01]');
                }
                if (validDataCount > 0) {
                    console.log('[CompsChart] ✅ K线图有', validDataCount, '个有效数据点');
                }
            }
        }
        else {
            var rawSeriesData = data.dataset[series.dataKey];
            values = ensureSeriesValues(data.categories, rawSeriesData);
            // 调试：检查堆叠柱状图数据
            if (process.env.NODE_ENV === 'development' && type === "bar" && series.stack) {
                console.log("[CompsChart] \u5806\u53E0\u67F1\u72B6\u56FE\u7CFB\u5217: ".concat(series.name, " (").concat(series.dataKey, ")"), {
                    id: series.id,
                    dataKey: series.dataKey,
                    stack: series.stack,
                    rawData: rawSeriesData,
                    processedValues: values,
                    categoriesLength: data.categories.length,
                    valuesLength: values.length,
                });
            }
        }
        var baseSeries = __assign(__assign({ id: series.id, name: series.name, type: type, data: values }, (isHorizontal && type === "candlestick"
            ? { xAxisIndex: 0, yAxisIndex: 0 } // 使用交换后的坐标轴配置
            : isHorizontal
                ? { xAxisIndex: 0, yAxisIndex: 0 }
                : { yAxisIndex: valueAxisIndex })), { axisId: axisId, emphasis: { focus: "series" } });
        // 调试：检查K线图系列配置
        if (process.env.NODE_ENV === 'development' && type === "candlestick") {
            var firstData = (_d = baseSeries.data) === null || _d === void 0 ? void 0 : _d[0];
            var openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
                ? Math.abs(firstData[1] - firstData[0])
                : 0;
            var lowHighDiff = firstData && Array.isArray(firstData) && firstData.length >= 4
                ? firstData[3] - firstData[2]
                : 0;
            console.log('[CompsChart] K线图系列配置:', {
                id: baseSeries.id,
                name: baseSeries.name,
                type: baseSeries.type,
                yAxisIndex: baseSeries.yAxisIndex,
                axisId: axisId,
                valueAxisIndex: valueAxisIndex,
                dataLength: (_e = baseSeries.data) === null || _e === void 0 ? void 0 : _e.length,
                firstDataPoint: (_f = baseSeries.data) === null || _f === void 0 ? void 0 : _f[0],
                openCloseDiff: openCloseDiff,
                lowHighDiff: lowHighDiff,
                isHorizontal: isHorizontal,
                // 检查数据格式
                dataFormatValid: (_g = baseSeries.data) === null || _g === void 0 ? void 0 : _g.every(function (d) {
                    return Array.isArray(d) && d.length >= 4 &&
                        typeof d[0] === 'number' && typeof d[1] === 'number' &&
                        typeof d[2] === 'number' && typeof d[3] === 'number';
                }),
            });
        }
        // 根据系列类型添加特定配置
        if (type === "candlestick") {
            // 注意：ECharts candlestick 不支持横向布局（类目在 Y 轴）
            // 如果 layout 是 horizontal，说明回退逻辑没有生效，这里再次检查并处理
            // 注意：ECharts的large模式可能不支持barWidth配置，导致K线图矩形主体不显示
            // 如果数据量不大（< 1000），禁用large模式以确保barWidth生效
            var dataLength = ((_h = baseSeries.data) === null || _h === void 0 ? void 0 : _h.length) || 0;
            var shouldUseLarge = dataLength >= 1000; // 只有数据量>=1000时才使用large模式
            if (isHorizontal) {
                // 横向布局时，由于我们交换了 X 轴和 Y 轴的配置
                // 现在 X 轴是 category（类目），Y 轴是 value（值）
                // 所以 ECharts 的 layout 应该是 'horizontal'（类目在 X 轴）
                baseSeries.layout = "horizontal"; // ECharts 的 horizontal = 类目在 X 轴
                // 只有在数据量很大时才使用large模式
                if (shouldUseLarge) {
                    baseSeries.large = true;
                    baseSeries.largeThreshold = 0;
                    baseSeries.progressive = 0;
                }
                else {
                    // 数据量不大时，禁用large模式以确保barWidth和itemStyle正确生效
                    baseSeries.large = false;
                }
                baseSeries.animation = false;
            }
            else {
                // 竖立布局时，根据数据量决定是否使用large模式
                if (shouldUseLarge) {
                    baseSeries.large = true;
                    baseSeries.largeThreshold = 0;
                    baseSeries.progressive = 0;
                }
                else {
                    // 数据量不大时，禁用large模式以确保barWidth和itemStyle正确生效
                    baseSeries.large = false;
                }
            }
            // K 线图配置
            // 如果用户没有提供 itemStyle，使用默认配置
            // 注意：确保K线图的主体（矩形）能够正确显示
            // ECharts K线图需要完整的itemStyle配置才能正确渲染矩形主体
            var defaultItemStyle = {
                color: "#00da3c",
                color0: "#ec0000",
                borderColor: "#333",
                borderColor0: "#333",
                borderWidth: 0,
                shadowBlur: 0,
                shadowColor: "transparent",
                shadowOffsetX: 0,
                shadowOffsetY: 0,
            };
            // 合并用户自定义配置
            baseSeries.itemStyle = __assign(__assign({}, defaultItemStyle), series.itemStyle);
            // 确保关键属性存在
            // 如果用户没有自定义borderWidth，使用默认值0（无边框）
            if (baseSeries.itemStyle.borderWidth === undefined) {
                baseSeries.itemStyle.borderWidth = 0; // 默认无边框
            }
            if (baseSeries.itemStyle.borderColor === undefined) {
                baseSeries.itemStyle.borderColor = "#333";
            }
            if (baseSeries.itemStyle.borderColor0 === undefined) {
                baseSeries.itemStyle.borderColor0 = "#333";
            }
            // 确保颜色设置正确
            if (baseSeries.itemStyle.color === undefined) {
                baseSeries.itemStyle.color = "#00da3c";
            }
            if (baseSeries.itemStyle.color0 === undefined) {
                baseSeries.itemStyle.color0 = "#ec0000";
            }
            // K 线图的宽度和间距配置，避免超出 Y 轴范围
            // 优先级：settings中的barWidth > series中的barWidth > 默认值
            // 这样可以让全局配置（settings）覆盖单个系列的配置
            var finalBarWidth = void 0;
            if (mergedSettings.barWidth !== undefined) {
                // settings中的barWidth优先级最高，可以覆盖series中的配置
                finalBarWidth = mergedSettings.barWidth;
            }
            else if (series.barWidth !== undefined) {
                finalBarWidth = series.barWidth;
            }
            else {
                // 默认宽度为类目宽度的 60%，确保不会超出边界
                finalBarWidth = "60%";
            }
            // 确保barWidth被正确设置
            baseSeries.barWidth = finalBarWidth;
            // ECharts K线图：如果barWidth是数字（像素值），同时设置barMaxWidth确保生效
            // 某些ECharts版本中，单独设置像素值可能不生效，需要配合barMaxWidth
            if (typeof finalBarWidth === 'number') {
                // 如果barWidth是像素值，设置barMaxWidth为barWidth的2倍，确保宽度生效
                baseSeries.barMaxWidth = finalBarWidth * 2;
            }
            else if (typeof finalBarWidth === 'string' && finalBarWidth.endsWith('%')) {
                // 如果是百分比，也设置一个合理的barMaxWidth（例如50像素）
                baseSeries.barMaxWidth = 50;
            }
            else {
                // 默认情况
                baseSeries.barMaxWidth = 50;
            }
            // 调试：检查K线图宽度配置
            if (process.env.NODE_ENV === 'development') {
                // 检查第一个数据点的开盘价和收盘价差异
                var firstData = (_j = baseSeries.data) === null || _j === void 0 ? void 0 : _j[0];
                var openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
                    ? Math.abs(firstData[1] - firstData[0])
                    : 0;
                console.log('[CompsChart] K线图宽度配置:', {
                    seriesId: series.id,
                    seriesName: series.name,
                    seriesBarWidth: series.barWidth,
                    settingsBarWidth: mergedSettings.barWidth,
                    finalBarWidth: baseSeries.barWidth,
                    barMaxWidth: baseSeries.barMaxWidth,
                    barWidthType: typeof baseSeries.barWidth,
                    openCloseDiff: openCloseDiff,
                    dataLength: (_k = baseSeries.data) === null || _k === void 0 ? void 0 : _k.length,
                    large: baseSeries.large,
                    baseSeriesConfig: {
                        type: baseSeries.type,
                        barWidth: baseSeries.barWidth,
                        barMaxWidth: baseSeries.barMaxWidth,
                        barCategoryGap: baseSeries.barCategoryGap,
                        large: baseSeries.large,
                        itemStyle: {
                            color: (_l = baseSeries.itemStyle) === null || _l === void 0 ? void 0 : _l.color,
                            color0: (_m = baseSeries.itemStyle) === null || _m === void 0 ? void 0 : _m.color0,
                            borderColor: (_o = baseSeries.itemStyle) === null || _o === void 0 ? void 0 : _o.borderColor,
                            borderColor0: (_p = baseSeries.itemStyle) === null || _p === void 0 ? void 0 : _p.borderColor0,
                            borderWidth: (_q = baseSeries.itemStyle) === null || _q === void 0 ? void 0 : _q.borderWidth,
                        },
                    },
                });
                // 如果开盘价和收盘价差异太小，给出警告
                if (openCloseDiff < 5) {
                    console.warn('[CompsChart] ⚠️ K线图开盘价和收盘价差异太小（' + openCloseDiff + '），矩形主体可能不明显。建议差异至少为10-20。');
                }
            }
            // 类目间的间距，默认 20%，确保左右两侧有足够空间
            baseSeries.barCategoryGap = (_s = (_r = series.barCategoryGap) !== null && _r !== void 0 ? _r : mergedSettings.barCategoryGap) !== null && _s !== void 0 ? _s : "20%";
            // 同一类目内的间距（如果有多个 K 线系列）
            if (series.barGap !== undefined) {
                baseSeries.barGap = series.barGap;
            }
            else if (mergedSettings.barGap !== undefined) {
                baseSeries.barGap = mergedSettings.barGap;
            }
        }
        else if (type === "line") {
            // 折线图：合并业务侧 lineStyle（虚线/颜色）；订单流速等可用常值 line 代替 markLine，规避 inverse 双轴竖线
            var ext = series;
            var fromStyle = ext.lineStyle && typeof ext.lineStyle === "object" ? ext.lineStyle : {};
            baseSeries.smooth =
                series.smooth !== undefined
                    ? series.smooth
                    : mergedSettings.smoothDefault;
            baseSeries.areaStyle =
                ((_t = series.area) !== null && _t !== void 0 ? _t : mergedSettings.areaDefault)
                    ? {}
                    : undefined;
            baseSeries.showSymbol =
                series.showSymbol !== undefined
                    ? series.showSymbol
                    : mergedSettings.showSymbolDefault;
            baseSeries.lineStyle = __assign({ width: 2 }, fromStyle);
            if (!baseSeries.lineStyle.color) {
                baseSeries.lineStyle.color = resolvedColor;
            }
            if (ext.color) {
                baseSeries.lineStyle.color = ext.color;
            }
            baseSeries.color = baseSeries.lineStyle.color;
            if (ext.z !== undefined)
                baseSeries.z = ext.z;
            if (ext.silent !== undefined)
                baseSeries.silent = ext.silent;
        }
        else if (type === "bar") {
            // 柱状图配置
            // 支持 stackId 或 stack 属性，优先使用 stackId（向后兼容）
            baseSeries.stack = series.stackId || series.stack;
            baseSeries.itemStyle = { color: resolvedColor };
            baseSeries.barWidth = series.barWidth;
            baseSeries.color = resolvedColor;
        }
        return baseSeries;
    });
    // 调试：检查输出的series数据
    if (process.env.NODE_ENV === 'development') {
        var klineSeriesInOutput = comboSeries.find(function (s) { return s.type === 'candlestick'; });
        console.log('[CompsChart] buildSeries 输出检查:', {
            totalSeries: comboSeries.length,
            hasKlineSeries: !!klineSeriesInOutput,
            klineSeriesInfo: klineSeriesInOutput ? {
                id: klineSeriesInOutput.id,
                type: klineSeriesInOutput.type,
                barWidth: klineSeriesInOutput.barWidth,
                barMaxWidth: klineSeriesInOutput.barMaxWidth,
                dataLength: (_a = klineSeriesInOutput.data) === null || _a === void 0 ? void 0 : _a.length,
            } : null,
            allSeriesTypes: comboSeries.map(function (s) { return ({ id: s.id, type: s.type }); }),
        });
    }
    return comboSeries;
}
function buildMarkLine(markLines, horizontal, swappedAxis, 
/** 纵向类目轴（含 inverse）时，单写 yAxis 易在边界误连出竖线，用首尾类目 coord 拉水平段 */
categories) {
    if (!markLines || markLines.length === 0) {
        return undefined;
    }
    var markLineData = markLines.map(function (config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        var lineStyle = config.color
            ? {
                color: config.color,
                type: (_b = (_a = config.lineStyle) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : "solid",
                width: (_d = (_c = config.lineStyle) === null || _c === void 0 ? void 0 : _c.width) !== null && _d !== void 0 ? _d : 1,
            }
            : {
                type: (_f = (_e = config.lineStyle) === null || _e === void 0 ? void 0 : _e.type) !== null && _f !== void 0 ? _f : "dashed",
                width: (_h = (_g = config.lineStyle) === null || _g === void 0 ? void 0 : _g.width) !== null && _h !== void 0 ? _h : 1,
            };
        var labelCfg = config.label
            ? {
                show: (_j = config.label.show) !== null && _j !== void 0 ? _j : true,
                position: (_k = config.label.position) !== null && _k !== void 0 ? _k : "insideEndTop",
                formatter: (_m = (_l = config.label.formatter) !== null && _l !== void 0 ? _l : config.name) !== null && _m !== void 0 ? _m : "",
            }
            : undefined;
        // 纵向 + 类目轴：用 [首类目,value]-[末类目,value]，避免 inverse / 边界下 yAxis 单点拉成贴边竖线
        if (!horizontal &&
            !swappedAxis &&
            Array.isArray(categories) &&
            categories.length >= 1) {
            var x0 = categories[0];
            var x1 = categories[categories.length - 1];
            var v = config.value;
            return [
                __assign({ name: config.name, silent: (_o = config.silent) !== null && _o !== void 0 ? _o : false, coord: [x0, v], lineStyle: lineStyle }, (labelCfg ? { label: labelCfg } : {})),
                {
                    coord: [x1, v],
                    lineStyle: lineStyle,
                },
            ];
        }
        var axisConfig;
        if (horizontal && swappedAxis) {
            axisConfig = { yAxis: config.value };
        }
        else if (horizontal) {
            axisConfig = { xAxis: config.value };
        }
        else {
            axisConfig = { yAxis: config.value };
        }
        var markLineItem = __assign(__assign(__assign({}, axisConfig), { name: config.name, silent: (_p = config.silent) !== null && _p !== void 0 ? _p : false, lineStyle: lineStyle }), (labelCfg ? { label: labelCfg } : {}));
        return markLineItem;
    });
    // 若任一配置为 silent，则整组 markLine 设为 silent（ECharts 以组为单位）
    var hasSilent = markLines.some(function (c) { return c.silent === true; });
    return {
        // 默认箭头会在贴边处连成竖向线段，inverse 类目轴时易被看成「右侧红线」
        symbol: ["none", "none"],
        data: markLineData,
        silent: hasSilent,
        emphasis: {
            lineStyle: {
                width: 2,
            },
        },
    };
}
// 根据 axisId 分组标记线配置
function groupMarkLinesByAxisId(markLines, axisIndexMap) {
    var grouped = new Map();
    markLines.forEach(function (config) {
        var _a;
        // 如果指定了 axisId，使用指定的 axisId
        // 如果没有指定，使用默认的 "left"
        var axisId = (_a = config.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
        if (!grouped.has(axisId)) {
            grouped.set(axisId, []);
        }
        grouped.get(axisId).push(config);
    });
    return grouped;
}
var comboChartAdapter = {
    type: "combo",
    buildOption: function (ctx) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
        var mergedSettings = __assign(__assign({}, mergeCategorySettings(ctx.settings)), { markLines: (_a = ctx.settings) === null || _a === void 0 ? void 0 : _a.markLines, layout: (_c = (_b = ctx.settings) === null || _b === void 0 ? void 0 : _b.layout) !== null && _c !== void 0 ? _c : "vertical" });
        var data = ctx.data;
        // 确保 data.series 存在且是数组
        if (!data.series || !Array.isArray(data.series)) {
            console.warn('[CompsChart] data.series 不存在或不是数组，返回空配置', { data: data });
            return {
                grid: { top: 0, left: 0, right: 0, bottom: 0 },
                tooltip: { show: false },
                legend: { show: false },
                xAxis: [],
                yAxis: [],
                series: [],
            };
        }
        var layout = (_d = mergedSettings.layout) !== null && _d !== void 0 ? _d : "vertical";
        var isHorizontal = layout === "horizontal";
        var hasCandlestick = data.series.some(function (s) { return s.type === "candlestick"; });
        var needSwapAxisForCandlestick = isHorizontal && hasCandlestick;
        var _7 = resolveAxisConfigs(data), initialYAxis = _7.yAxis, axisIndexMap = _7.axisIndexMap, sideOffsetMax = _7.sideOffsetMax;
        var series = buildSeries(ctx, mergedSettings, axisIndexMap, layout);
        // 确保 series 是数组
        if (!Array.isArray(series)) {
            console.warn('[CompsChart] buildSeries 返回的不是数组:', series);
            return {
                grid: { top: 0, left: 0, right: 0, bottom: 0 },
                tooltip: { show: false },
                legend: { show: false },
                xAxis: [],
                yAxis: [],
                series: [],
            };
        }
        var axisColorByIndex = new Map();
        series.forEach(function (item) {
            var _a, _b;
            var color = typeof item.color === "string" ? item.color : undefined;
            if (!color) {
                return;
            }
            var index = isHorizontal ? ((_a = item.xAxisIndex) !== null && _a !== void 0 ? _a : 0) : ((_b = item.yAxisIndex) !== null && _b !== void 0 ? _b : 0);
            var existing = axisColorByIndex.get(index);
            if (!existing || (item.type === "bar" && !existing.isBar) || (item.type === "candlestick" && !existing.isCandlestick)) {
                axisColorByIndex.set(index, {
                    color: color,
                    isBar: item.type === "bar",
                    isCandlestick: item.type === "candlestick",
                });
            }
        });
        if (!axisColorByIndex.has(0) && series.length > 0) {
            var first = series[0];
            var fallbackColor = typeof first.color === "string"
                ? first.color
                : DEFAULT_COLORS[0];
            axisColorByIndex.set(0, {
                color: fallbackColor,
                isBar: first.type === "bar",
                isCandlestick: first.type === "candlestick",
            });
        }
        var valueAxes = isHorizontal ? [initialYAxis[0]].filter(Boolean) : initialYAxis;
        var resolvedYAxis = valueAxes;
        if (((_e = ctx.settings) === null || _e === void 0 ? void 0 : _e.synchronizeAxisBounds) && !isHorizontal && data.series) {
            resolvedYAxis = attachAxisBoundsBySeries(valueAxes, data.series, data.dataset, data.categories);
        }
        // 强制右侧坐标轴从0开始（即使后端设置了其他值）
        resolvedYAxis = resolvedYAxis.map(function (axis) {
            if (axis.position === "right") {
                return __assign(__assign({}, axis), { min: 0 });
            }
            return axis;
        });
        // 右侧轴避免使用红色，以免与「年同比辅助线」等红色 markLine 混淆；红色时改用与平均价一致的浅蓝
        var isRedLike = function (hex) {
            if (!hex || typeof hex !== "string" || !hex.startsWith("#"))
                return false;
            var h = hex.replace(/^#/, "");
            var r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
            var g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
            var b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
            return r >= 200 && g <= 80 && b <= 80;
        };
        var rightAxisFallbackColor = "#73c0de"; // 平均价常用色，与红色辅助线区分
        var decoratedValueAxes = resolvedYAxis.map(function (axis, index) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            // 左侧坐标轴默认使用蓝色
            var leftAxisColor = "#1890ff"; // 蓝色
            var hint = (_a = axisColorByIndex.get(index)) !== null && _a !== void 0 ? _a : (index < DEFAULT_COLORS.length
                ? { color: DEFAULT_COLORS[index], isBar: false, isCandlestick: false }
                : undefined);
            // 如果是左侧轴，优先使用蓝色；否则使用从 series 推断的颜色
            var axisColor = axis.position === "left"
                ? leftAxisColor
                : (hint === null || hint === void 0 ? void 0 : hint.color);
            // 右侧轴若为红色系则改用浅蓝，避免与红色辅助线混成「一根红线无刻度」的误解
            if (axis.position === "right" && axisColor && isRedLike(axisColor)) {
                axisColor = rightAxisFallbackColor;
            }
            if (!axisColor) {
                return axis;
            }
            var axisWithColor = __assign({}, axis);
            var axisLineShow = ((_b = axisWithColor.axisLine) === null || _b === void 0 ? void 0 : _b.show) === false ? false : true;
            var explicitLineColor = (_d = (_c = axisWithColor.axisLine) === null || _c === void 0 ? void 0 : _c.lineStyle) === null || _d === void 0 ? void 0 : _d.color;
            var axisLineColor = (typeof explicitLineColor === "string" && explicitLineColor.length > 0)
                ? explicitLineColor
                : axisColor;
            // 关闭轴线时不要带 lineStyle.color，部分 ECharts 版本仍会描边；刻度一并关掉
            if (!axisLineShow) {
                axisWithColor.axisLine = { show: false };
                axisWithColor.axisTick = { show: false };
                // 避免残留纵向网格/次分割线被误看成「右侧红线」
                axisWithColor.splitLine = { show: false };
                axisWithColor.minorSplitLine = { show: false };
                var defaultFormatter_1 = axisWithColor.position === "right" && !((_e = axisWithColor.axisLabel) === null || _e === void 0 ? void 0 : _e.formatter)
                    ? function (value) { return Math.round(value).toString(); }
                    : undefined;
                var labelColor = axis.position === "right" ? rightAxisFallbackColor : axisLineColor;
                axisWithColor.axisLabel = __assign(__assign(__assign({}, (axisWithColor.axisLabel || {})), { show: ((_f = axisWithColor.axisLabel) === null || _f === void 0 ? void 0 : _f.show) !== false, color: labelColor }), (defaultFormatter_1 ? { formatter: defaultFormatter_1 } : {}));
                axisWithColor.nameTextStyle = __assign(__assign({}, (axisWithColor.nameTextStyle || {})), { color: labelColor });
                if (axis.position === "right" && axisWithColor.name !== undefined) {
                    axisWithColor.nameGap = (_g = axisWithColor.nameGap) !== null && _g !== void 0 ? _g : 12;
                    axisWithColor.show = axisWithColor.show !== false;
                }
                if ((_h = axisWithColor.splitLine) === null || _h === void 0 ? void 0 : _h.show) {
                    var rgba = hexToRgba(labelColor, 0.25);
                    axisWithColor.splitLine = __assign(__assign({}, axisWithColor.splitLine), { lineStyle: __assign(__assign({}, (((_j = axisWithColor.splitLine) === null || _j === void 0 ? void 0 : _j.lineStyle) || {})), { color: rgba !== null && rgba !== void 0 ? rgba : labelColor }) });
                }
                return axisWithColor;
            }
            axisWithColor.axisLine = __assign(__assign({}, (axisWithColor.axisLine || {})), { show: axisLineShow, lineStyle: __assign(__assign({}, (((_k = axisWithColor.axisLine) === null || _k === void 0 ? void 0 : _k.lineStyle) || {})), { color: axisLineColor }) });
            axisWithColor.axisTick = __assign(__assign({}, (axisWithColor.axisTick || {})), { show: ((_l = axisWithColor.axisTick) === null || _l === void 0 ? void 0 : _l.show) === false ? false : axisLineShow, length: 12, inside: true, lineStyle: { color: axisLineColor, width: 2 } });
            // 右侧价格轴默认去掉小数点，显示整数（如果没有自定义 formatter）；并强制显示刻度标签，避免「只有一根线无刻度」
            var defaultFormatter = axisWithColor.position === "right" && !((_m = axisWithColor.axisLabel) === null || _m === void 0 ? void 0 : _m.formatter)
                ? function (value) { return Math.round(value).toString(); }
                : undefined;
            axisWithColor.axisLabel = __assign(__assign(__assign({}, (axisWithColor.axisLabel || {})), { show: ((_o = axisWithColor.axisLabel) === null || _o === void 0 ? void 0 : _o.show) !== false, color: axisLineColor }), (defaultFormatter ? { formatter: defaultFormatter } : {}));
            axisWithColor.nameTextStyle = __assign(__assign({}, (axisWithColor.nameTextStyle || {})), { color: axisLineColor });
            // 右侧轴显式显示轴名称（如「平均价」），避免用户疑惑「这根轴给谁用、刻度是什么」
            if (axis.position === "right" && axisWithColor.name !== undefined) {
                axisWithColor.nameGap = (_p = axisWithColor.nameGap) !== null && _p !== void 0 ? _p : 12;
                axisWithColor.show = axisWithColor.show !== false;
            }
            if ((_q = axisWithColor.splitLine) === null || _q === void 0 ? void 0 : _q.show) {
                var rgba = hexToRgba(axisLineColor, 0.25);
                axisWithColor.splitLine = __assign(__assign({}, axisWithColor.splitLine), { lineStyle: __assign(__assign({}, (((_r = axisWithColor.splitLine) === null || _r === void 0 ? void 0 : _r.lineStyle) || {})), { color: rgba !== null && rgba !== void 0 ? rgba : axisLineColor }) });
            }
            return axisWithColor;
        });
        var hasBarOrCandlestick = (_f = series === null || series === void 0 ? void 0 : series.some(function (item) { return item.type === "bar" || item.type === "candlestick"; })) !== null && _f !== void 0 ? _f : false;
        // 根据图例位置计算 grid 偏移量
        var legendConfig = buildLegend(data.legend, mergedSettings.legend);
        var legendPosition = legendConfig.show === false
            ? null
            : ((_k = (_h = (_g = mergedSettings.legend) === null || _g === void 0 ? void 0 : _g.position) !== null && _h !== void 0 ? _h : (_j = data.legend) === null || _j === void 0 ? void 0 : _j.position) !== null && _k !== void 0 ? _k : "top");
        var LEGEND_MARGIN = 16; // 图例离外边距
        var LEGEND_GAP_HORIZONTAL = 24; // 图例在上下时，与绘制区之间的间距
        var LEGEND_GAP_VERTICAL = 16; // 图例在左右时，与绘制区之间的间距
        var LEGEND_HEIGHT = 24; // 水平图例的估算高度
        var LEGEND_WIDTH = 60; // 垂直图例的估算宽度
        var BASE_MARGIN = 16; // 绘制区到另一边的基础边距
        // 计算 grid 偏移量（已删除所有原有内边距）
        // 图例在上：top = 图例外边距(16) + 图例高度(24) + 间距(24) = 64, bottom = 16, left = 16, right = 16
        // 图例在下：top = 16, bottom = 间距(24) + 图例高度(24) + 图例外边距(16) = 64, left = 16, right = 16
        // 图例在左：top = 16, bottom = 16, left = 图例外边距(16) + 图例宽度(60) + 间距(16) = 92, right = 16
        // 图例在右：top = 16, bottom = 16, left = 16, right = 间距(16) + 图例宽度(60) + 图例外边距(16) = 92
        var gridTop;
        if (legendPosition === "top") {
            gridTop = LEGEND_MARGIN + LEGEND_HEIGHT + LEGEND_GAP_HORIZONTAL;
        }
        else if (legendPosition === "bottom") {
            gridTop = BASE_MARGIN;
        }
        else if (legendPosition === "left" || legendPosition === "right") {
            // 图例在左右时，上下边距为16
            gridTop = BASE_MARGIN;
        }
        else {
            gridTop = 40;
        }
        var gridBottom;
        if (legendPosition === "bottom") {
            gridBottom = LEGEND_GAP_HORIZONTAL + LEGEND_HEIGHT + LEGEND_MARGIN;
        }
        else if (legendPosition === "top") {
            gridBottom = BASE_MARGIN;
        }
        else if (legendPosition === "left" || legendPosition === "right") {
            // 图例在左右时，上下边距为16
            gridBottom = BASE_MARGIN;
        }
        else {
            gridBottom = 40;
        }
        var gridLeft;
        var gridRight;
        if (legendPosition === "top" || legendPosition === "bottom") {
            // 图例在上或下时，左右边距为16（删除所有原有内边距）
            gridLeft = BASE_MARGIN;
            // 如果有多个右侧Y轴，需要额外空间
            var rightAxisExtraSpace = sideOffsetMax.right > 0 ? sideOffsetMax.right + 40 : 0; // 每个偏移轴需要额外空间
            var originalRight = BASE_MARGIN + rightAxisExtraSpace;
            // 减少2/3，保留1/3
            gridRight = Math.max(Math.round(originalRight / 3), 0);
        }
        else {
            // 图例在左右时，删除原有内边距，只保留图例相关空间
            gridLeft = (legendPosition === "left" ? LEGEND_MARGIN + LEGEND_WIDTH + LEGEND_GAP_VERTICAL : BASE_MARGIN);
            // 如果有多个右侧Y轴，需要额外空间
            var rightAxisExtraSpace = sideOffsetMax.right > 0 ? sideOffsetMax.right + 40 : 0; // 每个偏移轴需要额外空间
            var baseRight = (legendPosition === "right" ? LEGEND_GAP_VERTICAL + LEGEND_WIDTH + LEGEND_MARGIN : BASE_MARGIN);
            var originalRight = baseRight + rightAxisExtraSpace;
            // 减少2/3，保留1/3
            gridRight = Math.max(Math.round(originalRight / 3), 0);
        }
        // 合并标记线配置（data 中的配置优先级更高）
        var markLines = (_l = data.markLines) !== null && _l !== void 0 ? _l : mergedSettings.markLines;
        // 根据 axisId 分组标记线，并为每个 axisId 构建对应的标记线配置
        var markLinesByAxisId = markLines && markLines.length > 0
            ? groupMarkLinesByAxisId(markLines, axisIndexMap)
            : new Map();
        // 如果有标记线，将标记线添加到对应的 series 中
        // 根据 axisId 找到对应的 series 并添加标记线
        // 每个 axisId 的标记线只添加到一个 series 上（优先 line 类型，其次是第一个 series）
        // 先找出每个 axisId 应该添加到哪个 series（优先 line 类型）
        var axisToSeriesIndex = new Map();
        if (!isHorizontal) {
            markLinesByAxisId.forEach(function (_, axisId) {
                var lineSeriesIndex = series.findIndex(function (s) {
                    var _a;
                    var sAxisId = (_a = s.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
                    return sAxisId === axisId && s.type === "line" && !s.markLine;
                });
                if (lineSeriesIndex >= 0) {
                    axisToSeriesIndex.set(axisId, lineSeriesIndex);
                }
                else {
                    var firstSeriesIndex = series.findIndex(function (s) {
                        var _a;
                        var sAxisId = (_a = s.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
                        return sAxisId === axisId && !s.markLine;
                    });
                    if (firstSeriesIndex >= 0) {
                        axisToSeriesIndex.set(axisId, firstSeriesIndex);
                    }
                }
            });
        }
        var markLineAttachIndexHorizontal = isHorizontal && markLines && markLines.length > 0
            ? Math.max(0, series.findIndex(function (s) { return s.type === "line"; }))
            : -1;
        // 调试：检查横向图表的 markLine 附加逻辑
        if (process.env.NODE_ENV === 'development' && isHorizontal && markLines && markLines.length > 0) {
            console.log('[CompsChart] 横向图表 markLine 附加检查:', {
                markLinesCount: markLines.length,
                markLines: markLines.map(function (ml) { return ({
                    value: ml.value,
                    name: ml.name,
                    axisId: ml.axisId,
                }); }),
                markLineAttachIndexHorizontal: markLineAttachIndexHorizontal,
                allLineSeries: series
                    .map(function (s, idx) { return ({ index: idx, id: s.id, type: s.type, axisId: s.axisId }); })
                    .filter(function (s) { return s.type === "line"; }),
            });
        }
        // 调试：检查series在finalSeries生成前的状态
        if (process.env.NODE_ENV === 'development') {
            var klineSeriesBeforeFinal = series.find(function (s) { return s.type === 'candlestick'; });
            console.log('[CompsChart] finalSeries生成前检查:', {
                totalSeries: series.length,
                hasKlineSeries: !!klineSeriesBeforeFinal,
                klineSeriesInfo: klineSeriesBeforeFinal ? {
                    id: klineSeriesBeforeFinal.id,
                    type: klineSeriesBeforeFinal.type,
                    barWidth: klineSeriesBeforeFinal.barWidth,
                } : null,
            });
        }
        var finalSeries = series.map(function (item, index) {
            var _a;
            if (item.markLine) {
                return item;
            }
            var seriesAxisId = (_a = item.axisId) !== null && _a !== void 0 ? _a : DEFAULT_AXIS_ID;
            if (markLineAttachIndexHorizontal >= 0 && index === markLineAttachIndexHorizontal) {
                var ml = buildMarkLine(markLines, isHorizontal, needSwapAxisForCandlestick, data.categories);
                if (ml)
                    return __assign(__assign({}, item), { markLine: ml });
                return item;
            }
            if (axisToSeriesIndex.get(seriesAxisId) === index) {
                var axisMarkLines = markLinesByAxisId.get(seriesAxisId);
                if (axisMarkLines) {
                    var markLineConfig = buildMarkLine(axisMarkLines, isHorizontal, needSwapAxisForCandlestick, data.categories);
                    if (markLineConfig) {
                        return __assign(__assign({}, item), { markLine: markLineConfig });
                    }
                }
            }
            return item;
        });
        // 调试：检查finalSeries生成后的状态
        if (process.env.NODE_ENV === 'development') {
            var klineSeriesAfterFinal = finalSeries.find(function (s) { return s.type === 'candlestick'; });
            console.log('[CompsChart] finalSeries生成后检查:', {
                totalSeries: finalSeries.length,
                hasKlineSeries: !!klineSeriesAfterFinal,
                klineSeriesInfo: klineSeriesAfterFinal ? {
                    id: klineSeriesAfterFinal.id,
                    type: klineSeriesAfterFinal.type,
                    barWidth: klineSeriesAfterFinal.barWidth,
                    barMaxWidth: klineSeriesAfterFinal.barMaxWidth,
                } : null,
                allSeriesTypes: finalSeries.map(function (s) { return ({ id: s.id, type: s.type }); }),
            });
        }
        // 构建 tooltip 配置，如果有标记线，需要将 trigger 改为 'item' 以支持 markLine 的 tooltip 和点击事件
        var tooltipConfig = buildTooltip(data.tooltip, mergedSettings.tooltip);
        var hasMarkLines = markLines && markLines.length > 0;
        // 如果有标记线，需要自定义 tooltip formatter 以支持 markLine
        var finalTooltip = tooltipConfig;
        if (hasMarkLines && tooltipConfig.show !== false) {
            var originalFormatter_1 = tooltipConfig.formatter;
            finalTooltip = __assign(__assign({}, tooltipConfig), { trigger: "item", formatter: function (params) {
                    var _a, _b, _c, _d, _e, _f, _g;
                    // 如果是 markLine，显示标记线的 tooltip
                    if (params.componentType === "markLine") {
                        var markLineName = ((_a = params.data) === null || _a === void 0 ? void 0 : _a.name) || "标记线";
                        var markLineValue = (_g = (_e = (_c = (_b = params.data) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : (_d = params.data) === null || _d === void 0 ? void 0 : _d.yAxis) !== null && _e !== void 0 ? _e : (_f = params.data) === null || _f === void 0 ? void 0 : _f.xAxis) !== null && _g !== void 0 ? _g : "";
                        return "".concat(markLineName, ": ").concat(markLineValue);
                    }
                    // 否则使用原始的 formatter 或默认格式
                    if (originalFormatter_1) {
                        return originalFormatter_1(params);
                    }
                    // 默认格式
                    if (Array.isArray(params)) {
                        // 多系列 tooltip
                        var result_1 = params[0].axisValue + "<br/>";
                        params.forEach(function (item) {
                            result_1 += "".concat(item.marker, " ").concat(item.seriesName, ": ").concat(item.value, "<br/>");
                        });
                        return result_1;
                    }
                    else {
                        // 单系列 tooltip
                        return "".concat(params.marker, " ").concat(params.seriesName, "<br/>").concat(params.name, ": ").concat(params.value);
                    }
                } });
        }
        var xAxis;
        var yAxis;
        // 对于横向布局且有 candlestick 的情况，交换 X 轴和 Y 轴的配置
        // 让 ECharts 认为类目在 X 轴，值在 Y 轴，从而绕过 candlestickLayout.js 的硬编码限制
        // 注意：这样显示出来的图表会是竖立的，但 candlestick 可以正常工作
        // 注意：needSwapAxisForCandlestick 已在上面定义（第770行），这里不再重复声明
        if (isHorizontal) {
            var valueAxis = decoratedValueAxes[0];
            // 尝试从 data.axes.bottom 获取配置（横向布局时，bottom 轴是价格轴）
            var bottomAxisConfig = (_m = data.axes) === null || _m === void 0 ? void 0 : _m.bottom;
            // 调试：检查 valueAxis 和 bottomAxisConfig 的 min/max 配置
            if (process.env.NODE_ENV === 'development') {
                console.log('[CompsChart] 横向布局轴配置:', {
                    hasValueAxis: !!valueAxis,
                    valueAxisMin: valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min,
                    valueAxisMax: valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max,
                    hasBottomAxisConfig: !!bottomAxisConfig,
                    bottomAxisMin: bottomAxisConfig === null || bottomAxisConfig === void 0 ? void 0 : bottomAxisConfig.min,
                    bottomAxisMax: bottomAxisConfig === null || bottomAxisConfig === void 0 ? void 0 : bottomAxisConfig.max,
                    needSwapAxisForCandlestick: needSwapAxisForCandlestick,
                });
            }
            // 优先使用 bottom 轴的配置（如果有），否则使用 valueAxis
            var priceAxisConfig = bottomAxisConfig || valueAxis;
            if (needSwapAxisForCandlestick) {
                // 交换：X 轴是 category（类目），Y 轴是 value（值）
                // 这样 candlestickLayout.js 就能正确识别维度了
                // 尝试从 data.axes.left（原始类目轴）获取 axisLabel 配置
                var categoryAxisConfig = (_o = data.axes) === null || _o === void 0 ? void 0 : _o.left;
                // 构建 axisLabel 配置，确保包含所有必要的属性
                var finalAxisLabel = (categoryAxisConfig === null || categoryAxisConfig === void 0 ? void 0 : categoryAxisConfig.axisLabel) ? __assign(__assign({}, categoryAxisConfig.axisLabel), { 
                    // 确保强制显示所有标签
                    interval: (_p = categoryAxisConfig.axisLabel.interval) !== null && _p !== void 0 ? _p : 0 }) : {
                    // 默认强制显示所有标签
                    interval: 0,
                };
                // 调试：检查 axisLabel 配置
                if (process.env.NODE_ENV === 'development') {
                    console.log('[CompsChart] 横向布局 + K线图：轴交换配置', {
                        hasCategoryAxisConfig: !!categoryAxisConfig,
                        categoryAxisConfigType: categoryAxisConfig === null || categoryAxisConfig === void 0 ? void 0 : categoryAxisConfig.type,
                        categoryAxisConfigData: categoryAxisConfig === null || categoryAxisConfig === void 0 ? void 0 : categoryAxisConfig.data,
                        categoriesCount: (_q = data.categories) === null || _q === void 0 ? void 0 : _q.length,
                        categories: (_r = data.categories) === null || _r === void 0 ? void 0 : _r.slice(0, 5),
                        finalAxisLabel: finalAxisLabel,
                        hasFormatter: !!(finalAxisLabel === null || finalAxisLabel === void 0 ? void 0 : finalAxisLabel.formatter),
                        formatterType: typeof (finalAxisLabel === null || finalAxisLabel === void 0 ? void 0 : finalAxisLabel.formatter),
                    });
                }
                xAxis = [
                    {
                        type: "category",
                        data: data.categories,
                        position: "bottom",
                        boundaryGap: hasBarOrCandlestick,
                        // 支持从类目轴配置中获取 axisLabel（用于文本换行等）
                        axisLabel: finalAxisLabel,
                    },
                ];
                yAxis = [
                    __assign(__assign({}, valueAxis), { type: "value", position: "left", 
                        // 优先使用 priceAxisConfig（bottom 轴）的 min/max，确保坐标轴范围正确应用
                        min: (typeof (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) === 'number' && Number.isFinite(priceAxisConfig.min))
                            ? priceAxisConfig.min
                            : ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== undefined && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== "dataMin" && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== null
                                ? priceAxisConfig.min
                                : ((valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== undefined && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== "dataMin" && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== null
                                    ? valueAxis.min
                                    : "dataMin")), max: (typeof (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) === 'number' && Number.isFinite(priceAxisConfig.max))
                            ? priceAxisConfig.max
                            : ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== undefined && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== "dataMax" && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== null
                                ? priceAxisConfig.max
                                : ((valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== undefined && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== "dataMax" && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== null
                                    ? valueAxis.max
                                    : "dataMax")), 
                        // 左侧价格轴强制去掉小数点，显示整数
                        axisLabel: __assign(__assign({}, ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.axisLabel) || (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.axisLabel) || {})), { formatter: function (value) {
                                // 始终格式化为整数，不显示小数
                                if (!Number.isFinite(value))
                                    return "0";
                                return Math.round(value).toString();
                            } }) }),
                ];
            }
            else {
                // 正常横向布局：X 轴是 value（值），Y 轴是 category（类目）
                xAxis = [
                    __assign(__assign({}, valueAxis), { type: "value", position: "bottom", 
                        // 优先使用 priceAxisConfig（bottom 轴）的 min/max，确保坐标轴范围正确应用
                        min: (typeof (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) === 'number' && Number.isFinite(priceAxisConfig.min))
                            ? priceAxisConfig.min
                            : ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== undefined && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== "dataMin" && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.min) !== null
                                ? priceAxisConfig.min
                                : ((valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== undefined && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== "dataMin" && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.min) !== null
                                    ? valueAxis.min
                                    : "dataMin")), max: (typeof (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) === 'number' && Number.isFinite(priceAxisConfig.max))
                            ? priceAxisConfig.max
                            : ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== undefined && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== "dataMax" && (priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.max) !== null
                                ? priceAxisConfig.max
                                : ((valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== undefined && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== "dataMax" && (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.max) !== null
                                    ? valueAxis.max
                                    : "dataMax")), 
                        // 底部价格轴默认去掉小数点，显示整数（如果没有自定义 formatter）
                        axisLabel: (((_s = priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.axisLabel) === null || _s === void 0 ? void 0 : _s.formatter) || ((_t = valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.axisLabel) === null || _t === void 0 ? void 0 : _t.formatter))
                            ? ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.axisLabel) || (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.axisLabel))
                            : __assign(__assign({}, ((priceAxisConfig === null || priceAxisConfig === void 0 ? void 0 : priceAxisConfig.axisLabel) || (valueAxis === null || valueAxis === void 0 ? void 0 : valueAxis.axisLabel) || {})), { formatter: function (value) { return Math.round(value).toString(); } }) }),
                ];
                yAxis = [
                    {
                        type: "category",
                        data: data.categories,
                        position: "left",
                        boundaryGap: hasBarOrCandlestick,
                    },
                ];
            }
        }
        else {
            // 检查是否有bottom轴的axisLabel配置（用于控制X轴标签显示）
            var bottomAxisConfig = (_u = data.axes) === null || _u === void 0 ? void 0 : _u.bottom;
            var xAxisLabelConfig = (bottomAxisConfig === null || bottomAxisConfig === void 0 ? void 0 : bottomAxisConfig.axisLabel) ? __assign(__assign({}, bottomAxisConfig.axisLabel), { 
                // 确保强制显示所有标签
                interval: (_v = bottomAxisConfig.axisLabel.interval) !== null && _v !== void 0 ? _v : 0 }) : undefined;
            // inverse: true 时类目轴反转，用于订单流速等「最右侧为当前时间、往左为过去」的时序
            var xAxisInverse = (bottomAxisConfig === null || bottomAxisConfig === void 0 ? void 0 : bottomAxisConfig.inverse) === true;
            xAxis = [
                __assign(__assign({ type: "category", data: data.categories, boundaryGap: hasBarOrCandlestick }, (xAxisInverse ? { inverse: true } : {})), (xAxisLabelConfig ? { axisLabel: xAxisLabelConfig } : {})),
            ];
            yAxis = decoratedValueAxes;
        }
        var option = {
            textStyle: {
                fontFamily: '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif',
            },
            grid: {
                top: gridTop,
                left: gridLeft,
                right: gridRight,
                bottom: gridBottom,
                containLabel: true,
            },
            tooltip: finalTooltip,
            legend: legendConfig,
            xAxis: xAxis,
            yAxis: yAxis,
            series: finalSeries,
        };
        // 调试：检查最终配置
        if (process.env.NODE_ENV === 'development') {
            var klineSeries = (_w = option.series) === null || _w === void 0 ? void 0 : _w.find(function (s) { return s.type === 'candlestick'; });
            var firstKlineData = (_x = klineSeries === null || klineSeries === void 0 ? void 0 : klineSeries.data) === null || _x === void 0 ? void 0 : _x[0];
            var openCloseDiff = firstKlineData && Array.isArray(firstKlineData) && firstKlineData.length >= 2
                ? Math.abs(firstKlineData[1] - firstKlineData[0])
                : 0;
            console.log('[CompsChart] 最终ECharts配置检查:', {
                hasKlineSeries: !!klineSeries,
                klineSeriesConfig: klineSeries ? {
                    id: klineSeries.id,
                    name: klineSeries.name,
                    type: klineSeries.type,
                    yAxisIndex: klineSeries.yAxisIndex,
                    axisId: klineSeries.axisId,
                    dataLength: (_y = klineSeries.data) === null || _y === void 0 ? void 0 : _y.length,
                    firstDataPoint: (_z = klineSeries.data) === null || _z === void 0 ? void 0 : _z[0],
                    openCloseDiff: openCloseDiff,
                    barWidth: klineSeries.barWidth,
                    barMaxWidth: klineSeries.barMaxWidth,
                    barCategoryGap: klineSeries.barCategoryGap,
                    barGap: klineSeries.barGap,
                    itemStyle: {
                        color: (_0 = klineSeries.itemStyle) === null || _0 === void 0 ? void 0 : _0.color,
                        color0: (_1 = klineSeries.itemStyle) === null || _1 === void 0 ? void 0 : _1.color0,
                        borderColor: (_2 = klineSeries.itemStyle) === null || _2 === void 0 ? void 0 : _2.borderColor,
                        borderColor0: (_3 = klineSeries.itemStyle) === null || _3 === void 0 ? void 0 : _3.borderColor0,
                        borderWidth: (_4 = klineSeries.itemStyle) === null || _4 === void 0 ? void 0 : _4.borderWidth,
                    },
                    layout: klineSeries.layout,
                    large: klineSeries.large,
                    // 完整的系列配置（用于调试）
                    fullSeriesConfig: JSON.stringify(klineSeries, null, 2).substring(0, 1000), // 只显示前1000字符
                } : null,
                yAxisCount: yAxis === null || yAxis === void 0 ? void 0 : yAxis.length,
                yAxisConfigs: yAxis === null || yAxis === void 0 ? void 0 : yAxis.map(function (axis, index) { return ({
                    index: index,
                    position: axis.position,
                    type: axis.type,
                    id: axis.id,
                    min: axis.min,
                    max: axis.max,
                }); }),
                inputAxes: data.axes,
                totalSeriesCount: (_5 = option.series) === null || _5 === void 0 ? void 0 : _5.length,
                seriesTypes: (_6 = option.series) === null || _6 === void 0 ? void 0 : _6.map(function (s) { return ({
                    id: s.id,
                    type: s.type,
                    yAxisIndex: s.yAxisIndex,
                    axisId: s.axisId,
                }); }),
                xAxisInfo: {
                    count: xAxis === null || xAxis === void 0 ? void 0 : xAxis.length,
                    configs: xAxis === null || xAxis === void 0 ? void 0 : xAxis.map(function (axis, index) {
                        var _a, _b, _c;
                        return ({
                            index: index,
                            type: axis.type,
                            position: axis.position,
                            dataCount: Array.isArray(axis.data) ? axis.data.length : 0,
                            dataSample: Array.isArray(axis.data) ? axis.data.slice(0, 5) : axis.data,
                            hasAxisLabel: !!axis.axisLabel,
                            axisLabelInterval: (_b = (_a = axis.axisLabel) === null || _a === void 0 ? void 0 : _a.interval) !== null && _b !== void 0 ? _b : 0,
                            hasFormatter: !!((_c = axis.axisLabel) === null || _c === void 0 ? void 0 : _c.formatter),
                        });
                    }),
                },
                yAxisInfo: {
                    count: yAxis === null || yAxis === void 0 ? void 0 : yAxis.length,
                    configs: yAxis === null || yAxis === void 0 ? void 0 : yAxis.map(function (axis, index) { return ({
                        index: index,
                        type: axis.type,
                        position: axis.position,
                        min: axis.min,
                        max: axis.max,
                    }); }),
                },
            });
        }
        var dataZoom = buildDataZoom(data.dataZoom, mergedSettings.dataZoom);
        if (dataZoom) {
            if (isHorizontal) {
                dataZoom = dataZoom.map(function (dz) { return (__assign(__assign({}, dz), { yAxisIndex: 0 })); });
            }
            option.dataZoom = dataZoom;
        }
        // 后处理：确保所有 candlestick 系列在横向布局时都正确配置
        // 确保K线图的barWidth配置在最终配置中正确设置
        if (option.series) {
            option.series.forEach(function (series) {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                if (series.type === "candlestick") {
                    // 确保barWidth配置存在且正确
                    // settings中的barWidth优先级最高，始终覆盖series中的配置
                    if (mergedSettings.barWidth !== undefined) {
                        series.barWidth = mergedSettings.barWidth;
                    }
                    else if (!series.barWidth) {
                        // 如果settings和series都没有设置，使用默认值
                        series.barWidth = 24; // 默认24像素
                    }
                    // 确保barMaxWidth也被设置（某些ECharts版本需要）
                    // 注意：barMaxWidth必须在所有布局中都设置，不仅仅是横向布局
                    if (!series.barMaxWidth) {
                        if (typeof series.barWidth === 'number') {
                            series.barMaxWidth = series.barWidth * 2;
                        }
                        else {
                            series.barMaxWidth = 50; // 默认最大宽度50像素
                        }
                    }
                    // 确保itemStyle完整配置（某些ECharts版本需要完整的itemStyle才能正确渲染）
                    if (!series.itemStyle) {
                        series.itemStyle = {};
                    }
                    // 确保关键属性存在
                    if (!series.itemStyle.color) {
                        series.itemStyle.color = "#00da3c";
                    }
                    if (!series.itemStyle.color0) {
                        series.itemStyle.color0 = "#ec0000";
                    }
                    // 如果用户没有自定义borderWidth，使用默认值0（无边框）
                    if (series.itemStyle.borderWidth === undefined) {
                        series.itemStyle.borderWidth = 0; // 默认无边框
                    }
                    if (!series.itemStyle.borderColor) {
                        series.itemStyle.borderColor = "#333";
                    }
                    if (!series.itemStyle.borderColor0) {
                        series.itemStyle.borderColor0 = "#333";
                    }
                    // 横向布局时的特殊处理
                    if (isHorizontal) {
                        // 横向布局时，根据数据量决定是否使用large模式
                        // large模式可能不支持barWidth，导致K线图矩形主体不显示
                        var dataLength = ((_a = series.data) === null || _a === void 0 ? void 0 : _a.length) || 0;
                        var shouldUseLarge = dataLength >= 1000;
                        if (series.large === undefined) {
                            series.large = shouldUseLarge;
                        }
                        if (series.large && series.largeThreshold === undefined) {
                            series.largeThreshold = 0;
                        }
                        // 只有在使用large模式时才设置progressive
                        if (series.large && series.progressive === undefined) {
                            series.progressive = 0;
                        }
                        if (series.animation === undefined) {
                            series.animation = false;
                        }
                        // 确保 layout 设置正确（buildSeries 中已设置为 "horizontal"）
                        if (!series.layout) {
                            series.layout = "horizontal"; // ECharts 的 horizontal = 类目在 X 轴
                        }
                    }
                    // 强制确保K线图的关键配置存在（某些ECharts版本需要）
                    // 确保data是有效的二维数组
                    if (!Array.isArray(series.data) || series.data.length === 0) {
                        console.warn('[CompsChart] ⚠️ K线图数据无效:', {
                            id: series.id,
                            dataType: typeof series.data,
                            dataIsArray: Array.isArray(series.data),
                            dataLength: (_b = series.data) === null || _b === void 0 ? void 0 : _b.length,
                        });
                    }
                    else {
                        // 验证数据格式
                        var firstData = series.data[0];
                        if (!Array.isArray(firstData) || firstData.length < 4) {
                            console.warn('[CompsChart] ⚠️ K线图数据格式错误:', {
                                id: series.id,
                                firstData: firstData,
                                expectedFormat: '[open, close, low, high]',
                            });
                        }
                    }
                    // 调试：检查最终K线图配置
                    if (process.env.NODE_ENV === 'development') {
                        var firstData = (_c = series.data) === null || _c === void 0 ? void 0 : _c[0];
                        var openCloseDiff = firstData && Array.isArray(firstData) && firstData.length >= 2
                            ? Math.abs(firstData[1] - firstData[0])
                            : 0;
                        console.log('[CompsChart] 最终K线图配置:', {
                            id: series.id,
                            name: series.name,
                            type: series.type,
                            barWidth: series.barWidth,
                            barMaxWidth: series.barMaxWidth,
                            barCategoryGap: series.barCategoryGap,
                            barGap: series.barGap,
                            itemStyle: {
                                color: (_d = series.itemStyle) === null || _d === void 0 ? void 0 : _d.color,
                                color0: (_e = series.itemStyle) === null || _e === void 0 ? void 0 : _e.color0,
                                borderColor: (_f = series.itemStyle) === null || _f === void 0 ? void 0 : _f.borderColor,
                                borderColor0: (_g = series.itemStyle) === null || _g === void 0 ? void 0 : _g.borderColor0,
                                borderWidth: (_h = series.itemStyle) === null || _h === void 0 ? void 0 : _h.borderWidth,
                            },
                            layout: series.layout,
                            isHorizontal: isHorizontal,
                            dataLength: (_j = series.data) === null || _j === void 0 ? void 0 : _j.length,
                            firstDataPoint: firstData,
                            openCloseDiff: openCloseDiff,
                            yAxisIndex: series.yAxisIndex,
                            large: series.large,
                            largeThreshold: series.largeThreshold,
                        });
                        // 如果开盘价和收盘价差异太小，给出警告
                        if (openCloseDiff < 10) {
                            console.warn('[CompsChart] ⚠️ K线图开盘价和收盘价差异太小（' + openCloseDiff + '），矩形主体可能不明显。建议差异至少为20-30。');
                        }
                    }
                }
            });
        }
        // 订单流速：右轴常值虚线系列在双轴 + 类目 inverse 下，偶发与 emphasis/图例联动画出贴边红色竖线
        if (option.series) {
            option.series.forEach(function (s) {
                if ((s === null || s === void 0 ? void 0 : s.id) === "order-traffic-comparison-ref") {
                    s.emphasis = { disabled: true };
                    s.legendHoverLink = false;
                    delete s.markLine;
                    delete s.markPoint;
                }
            });
        }
        return option;
    },
    getDefaultSettings: function () { return ({
        legend: { show: true },
        tooltip: { enable: true, trigger: "axis" },
        dataZoom: { enable: false, start: 0, end: 100 },
        areaDefault: false,
        smoothDefault: true,
        synchronizeAxisBounds: false,
        showSymbolDefault: false,
        layout: "vertical",
    }); },
};
export default comboChartAdapter;
